// Things companions put into the world: furniture, slime blocks, portals, wormholes, a grappling
// hook, a thrown file, the party's speaker and lights.
// They are only drawn, never part of the apps underneath. Each has an `owner`.

import { clockNow } from './clock.js';
import { env } from './core.js';
import { blit, bounds, linePx } from './pixels.js';
import { mixHex } from './color.js';
import { chairPixels, castlePixels, slimePixels, obsidianPixels, portalFill, bedBackPixels, blanketPixels, ITEMS, FILE,
  speakerPixels, standPixels, STAND, PARTY_COLORS, HOOK_GUN, HOOK, ROPE, wormholePixels, discPixels } from './props.js';
import { home, castleLeft, castleScale, floorY } from './world.js';
import { handPoint } from './companion.js';

export const props = new Set();
const CHAIR = chairPixels(), CASTLE = castlePixels(), BED = bedBackPixels();

// A companion's things go when its action is interrupted.
export function clearProps(owner) { for (const p of props) if (p.owner === owner) props.delete(p); }

export function drawStage(now) {
  if (home()) blit(CASTLE, castleLeft(), floorY(), castleScale(), now, null);
  for (const p of props) {
    const ctx = env.ctx;
    if (p.ledge) {                       // stays on its surface when the window moves
      const g = p.ledge.geom();
      if (g) { p.x = g.x1 + p.dx; p.y = g.y; }
    }
    ctx.globalAlpha = p.alpha ?? 1;
    if (p.kind === 'pearl') {
      blit(ITEMS.pearl, p.x, p.y, p.s, now, null);
    } else if (p.kind === 'chair' || p.kind === 'bed') {
      // It unfolds from the floor up: rows above `reveal` aren't drawn yet.
      blit((p.kind === 'bed' ? BED : CHAIR).filter(([, r]) => r >= -p.reveal), p.x, p.y, p.s, now, null);
    } else if (p.kind === 'file') {                    // a thrown file, spinning
      ctx.save();
      ctx.translate(Math.round(p.x), Math.round(p.y)); ctx.rotate(p.rot || 0);
      blit(FILE, -1.5 * p.s, -0.5 * p.s, p.s, now, null);
      ctx.restore();
    } else if (p.kind === 'slime') {
      const b = p.b, k = p.scale ?? 1;
      const w = b * p.s * k, h = b * p.s * k * (p.squish ?? 1);
      ctx.save();
      ctx.translate(Math.round(p.x - w / 2), Math.round(p.y - h));
      ctx.scale(k, k * (p.squish ?? 1));
      ctx.globalAlpha = (p.alpha ?? 1) * 0.92;
      blit(slimePixels(b), 0, 0, p.s, now, null);
      ctx.restore();
    } else if (p.kind === 'portal') {
      drawPortalInside(p, now);         // the frame goes in front of the crab: drawStageFront
    } else if (p.kind === 'speaker') {
      const b = playing(p, now), pump = b ? Math.max(0, 1 - b.f * 4) : 0;
      blit(speakerPixels(pump, !!b).filter(([, r]) => r >= -p.reveal), p.x, p.y, p.s, now, null);
    } else if (p.kind === 'stand') {
      const b = playing(p, now);
      if (b) drawBeams(p, now, b);
      blit(standPixels(b ? b.n : 0, p.lit).filter(([, r]) => r >= -p.reveal), p.x, p.y, p.s, now, null);
    } else if (p.kind === 'partyLight') {
      drawPartyLight(p, now, 'back');
    } else if (p.kind === 'wormhole') {
      drawWormhole(p, now);
    } else if (p.draw) {                // something that draws itself (a pest, a slime's little split-off slimes)
      p.draw(now);
    }
    ctx.globalAlpha = 1;
  }
}

// A portal: an obsidian frame 4 blocks wide and 5 tall, standing on (x, y) with its middle at x.
// p.blocks is how many frame blocks are placed so far, p.lit how full the swirl is (0..1).
// A full frame, corners included, in the order a player builds it: the bottom row, both sides
// upward, then the top row.
const BLOCKS = [[0, 4], [1, 4], [2, 4], [3, 4], [0, 3], [0, 2], [0, 1], [3, 3], [3, 2], [3, 1], [0, 0], [1, 0], [2, 0], [3, 0]];
export const portalBlocks = () => BLOCKS;

// Drawn after the companions: a portal's frame (so it stands inside the frame), a bed's
// blanket (so it lies under it) and a grappling hook's gun (in its owner's hand).
export function drawStageFront(now) {
  for (const p of props) {
    if (p.kind === 'portal') drawPortalFrame(p, now);
    else if (p.kind === 'hook') drawHook(p, now);
    else if (p.kind === 'bed' && p.reveal >= 14) blit(blanketPixels(p.breathIn), p.x, p.y, p.s, now, null);
    else if (p.kind === 'partyLight') drawPartyLight(p, now, 'front');
    else if (p.drawFront) {             // something an ability draws in front of everyone (a speech bubble)
      env.ctx.globalAlpha = p.alpha ?? 1;
      p.drawFront(now);
      env.ctx.globalAlpha = 1;
    }
  }
}

// ---------- party lights ----------
// The beat of the party a prop belongs to, or null when the music is off.
const playing = (p, now) => (p.party?.live() ? p.party.beat(now) : null);
const partyColor = k => PARTY_COLORS[((k % PARTY_COLORS.length) + PARTY_COLORS.length) % PARTY_COLORS.length];

// A soft cone of light from (x0, y0), w0 wide, down to a strip w1 wide at y1, with a glow where it lands.
function cone(x0, y0, w0, x1, y1, w1, color, alpha) {
  const ctx = env.ctx;
  ctx.fillStyle = color;
  ctx.globalAlpha = alpha;
  ctx.beginPath();
  ctx.moveTo(x0 - w0 / 2, y0); ctx.lineTo(x0 + w0 / 2, y0); ctx.lineTo(x1 + w1 / 2, y1); ctx.lineTo(x1 - w1 / 2, y1);
  ctx.closePath(); ctx.fill();
  ctx.globalAlpha = Math.min(1, alpha * 2.2);
  ctx.beginPath(); ctx.ellipse(x1, y1, w1 / 2, Math.max(2, w1 / 9), 0, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = 1;
}

// The stand's two lamps sweep their beams back and forth across the surface it stands on.
function drawBeams(p, now, b) {
  const s = p.s, sweep = ((now - p.party.t0) / (p.party.beatMs * 8)) * Math.PI * 2;
  STAND.lamps.slice(0, p.lit).forEach(([c, r], i) => {
    const x0 = p.x + (c + 1) * s, y0 = p.y + (r + 2) * s;
    const angle = (i ? 0.3 : -0.3) + 0.5 * Math.sin(sweep + i * Math.PI);
    const x1 = x0 + Math.tan(angle) * (p.y - y0);
    cone(x0, y0, 1.5 * s, x1, p.y, 14 * s, partyColor(b.n + i * 3), b.f < 0.15 ? 0.24 : 0.16);
  });
}

// Every dancer gets its own lights: a colored spotlight from above and little lights circling
// around it. They change color on every beat. The circle is drawn in two halves, behind and
// in front of the dancer.
function drawPartyLight(p, now, layer) {
  const me = p.of, b = playing(p, now);
  if (!b || me.alpha <= 0) return;
  const s = me.s, ctx = env.ctx, height = (me.shape.bh + me.shape.leg) * s;
  if (layer === 'back') cone(me.x, me.y - height - 20 * s, 2 * s, me.x, me.y, 18 * s, partyColor(b.n + p.hue), b.f < 0.15 ? 0.2 : 0.13);
  const turn = ((b.n + b.f) / 2) * Math.PI * 2, rx = (me.shape.bw / 2 + 4) * s, ry = 2.5 * s, cy = me.y - height / 2 - s;
  for (let k = 0; k < 5; k++) {
    const a = turn + (k / 5) * Math.PI * 2, behind = Math.sin(a) < 0;
    if (behind !== (layer === 'back')) continue;
    const x = Math.round(me.x + Math.cos(a) * rx), y = Math.round(cy + Math.sin(a) * ry);
    const color = partyColor(b.n + k + p.hue), u = Math.max(2, Math.round(s * 0.8));
    ctx.globalAlpha = 0.3; ctx.fillStyle = color;
    ctx.fillRect(x - u, y - u, 3 * u, 3 * u);
    ctx.globalAlpha = 1; ctx.fillStyle = b.f < 0.2 ? mixHex(color, 1.4) : color;
    ctx.fillRect(x, y, u, u);
  }
}

function drawPortalInside(p, now) {
  const bs = p.b * p.s, left = Math.round(p.x - 2 * bs), top = Math.round(p.y - 5 * bs);
  if (p.lit > 0) {
    const w = 2 * p.b, h = 3 * p.b, shown = Math.round(h * p.lit);
    const fill = portalFill(w, h, now, p.seed).filter(([, r]) => r >= h - shown);
    env.ctx.globalAlpha = (p.alpha ?? 1) * 0.86;
    blit(fill, left + bs, top + bs, p.s, now, null);
    env.ctx.globalAlpha = p.alpha ?? 1;
  }
}

function drawPortalFrame(p, now) {
  const bs = p.b * p.s, left = Math.round(p.x - 2 * bs), top = Math.round(p.y - 5 * bs);
  BLOCKS.slice(0, p.blocks).forEach(([c, r], i) => {
    const k = p.shrink?.[i] ?? 1;
    if (k <= 0) return;
    const size = p.b * p.s * k, ox = left + c * bs + (bs - size) / 2, oy = top + r * bs + (bs - size) / 2;
    env.ctx.save();
    env.ctx.translate(Math.round(ox), Math.round(oy));
    env.ctx.scale(k, k);
    blit(obsidianPixels(p.b, i + 1), 0, 0, p.s, now, null);
    env.ctx.restore();
  });
}

// ---------- wormhole ----------
// A swirling oval standing on (x, y) (see wormholePixels), p.open how far it's open; p.disc is
// where the disc that opens it is on its way ({ x, y }).
function drawWormhole(p, now) {
  if (p.disc) blit(discPixels(now), p.disc.x, p.disc.y, p.s, now, null);
  if (p.open > 0.02) blit(wormholePixels(p.w, p.h, p.open, now, p.seed), p.x, p.y, p.s, now, null);
}

// ---------- grappling hook ----------
// The gun in its owner's hand (p.of, p.arm), the rope and the hook. p.hook is where the hook's
// tip is and which way it points ({ x, y, angle }), or null while it's in the gun (p.loaded shows
// its tip at the muzzle). The gun points along p.aim (radians, 0 = right) if set, else at the
// hook, else at p.target(). It kicks up when it fires (p.kick: when), and the rope hangs p.sag
// pixels down in the middle.
function gunAngle(p, now) {
  const h = handPoint(p.of, p.arm), at = p.hook || p.target?.();
  const a = p.aim ?? (at ? Math.atan2(at.y - h.y, at.x - h.x) : 0);
  const kick = p.kick ? Math.max(0, 1 - (now - p.kick) / 160) : 0;
  return a - (Math.cos(a) < 0 ? -1 : 1) * 0.4 * kick;
}
// A pixel of the gun's drawing ([col, row], pointing right) on screen: turned with the gun, and
// flipped so it stays upright when it points left.
function onGun(p, [c, r], now) {
  const h = handPoint(p.of, p.arm), a = gunAngle(p, now), f = Math.cos(a) < 0 ? -1 : 1, s = p.of.s;
  return { x: h.x + (c * Math.cos(a) - r * f * Math.sin(a)) * s, y: h.y + (c * Math.sin(a) + r * f * Math.cos(a)) * s };
}
export const gunMuzzle = (p, now = clockNow()) => onGun(p, HOOK_GUN.muzzle, now);

function drawHook(p, now) {
  const me = p.of, s = me.s;
  if (me.alpha <= 0) return;
  if (p.hook) {
    const { x, y, angle } = p.hook, ring = { x: x + Math.cos(angle) * HOOK.ring[0] * s, y: y + Math.sin(angle) * HOOK.ring[0] * s };
    drawRope(gunMuzzle(p, now), ring, p.sag || 0, s);
    blitTurned(HOOK.art, x, y, s, angle, now);
  }
  const a = gunAngle(p, now), h = handPoint(me, p.arm);
  const art = p.loaded ? [...HOOK_GUN.art, [...HOOK_GUN.muzzle, HOOK_GUN.loaded]] : HOOK_GUN.art;
  blitTurned(Math.cos(a) < 0 ? art.map(([c, r, t]) => [c, -r, t]) : art, h.x, h.y, s, a, now);
}

// A rope from screen point m to r, hanging `sag` pixels lower in the middle, in pixels as big
// as the companion's that alternate between two shades.
function drawRope(m, r, sag, s) {
  const n = Math.max(1, Math.ceil(Math.hypot(r.x - m.x, r.y - m.y) / s));
  const cx = (m.x + r.x) / 2, cy = (m.y + r.y) / 2 + 2 * sag;   // the curve's control point
  const px = [];
  let last = null;
  for (let i = 0; i <= n; i++) {
    const t = i / n, u = 1 - t;
    const x = u * u * m.x + 2 * u * t * cx + t * t * r.x, y = u * u * m.y + 2 * u * t * cy + t * t * r.y;
    const cell = [Math.round((x - m.x) / s), Math.round((y - m.y) / s)];
    if (last && cell[0] === last[0] && cell[1] === last[1]) continue;
    for (const [c, k] of last ? linePx(last[0], last[1], cell[0], cell[1], '').slice(1) : [cell]) px.push([c, k, ROPE[(px.length >> 1) % 2]]);
    last = cell;
  }
  blit(px, m.x - s / 2, m.y - s / 2, s, 0, null);
}

// Pixels turned by angle (radians, clockwise) around their (0, 0) pixel, whose middle goes at
// screen point (x, y). They're drawn upright into a spare canvas first and placed turned, so the
// edges stay straight lines (like a block limb's).
const spare = document.createElement('canvas'), spareCtx = spare.getContext('2d');
function blitTurned(px, x, y, s, angle, now) {
  const b = bounds(px), w = (b.c1 - b.c0 + 1) * s, h = (b.r1 - b.r0 + 1) * s, dpr = window.devicePixelRatio || 1;
  spare.width = Math.ceil(w * dpr); spare.height = Math.ceil(h * dpr);
  spareCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  spareCtx.clearRect(0, 0, w, h);
  blit(px, -b.c0 * s, -b.r0 * s, s, now, null, null, spareCtx);
  const ctx = env.ctx, half = Math.floor(s / 2);
  ctx.save();
  ctx.translate(Math.round(x), Math.round(y)); ctx.rotate(angle);
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(spare, b.c0 * s - half, b.r0 * s - half, w, h);
  ctx.restore();
}
